import { ChartDataSets } from 'chart.js'
import moment, { Moment } from 'moment'
import React, {
  FunctionComponent,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { Bar } from 'react-chartjs-2'
import { useTranslation } from 'react-i18next'

import { DashboardCardBottomLeftContainer, DashBoardCardTitle } from '../styles'

import { Client } from './Client/Client'
import { IRevenueResult } from './Client/IRevenue'
import { NoRevenueLabel, RevenueCardContainer } from './style'

export const RevenueCard: FunctionComponent = () => {
  const [chartData, setChartData] = useState({
    labels: [],
    datasets: [],
  })
  const [startDate] = useState<Moment>(moment().startOf('month'))
  const [from] = useState<Moment>(startDate.clone().subtract(11, 'months'))

  const [to] = useState<Moment>(startDate.clone().endOf('month'))
  const [revenueArray, setRevenueArray] = useState<IRevenueResult | undefined>()

  const { t } = useTranslation()
  const chartRef = useRef(null)

  const [windowHeight, setWindowHeight] = useState(window.innerHeight)

  useEffect(() => {
    const handleResize = () => {
      setWindowHeight(window.innerHeight)
    }

    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [])

  useEffect(() => {
    Client.getRevenue().then(newRevenue => {
      setRevenueArray(newRevenue)
    })
  }, [from, to])

  const [labels, values] = useMemo(() => {
    if (typeof revenueArray === 'undefined') {
      return [[], []]
    }

    const months = []
    const currentDate = moment().subtract(11, 'month').startOf('month')
    const endDate = moment().startOf('month')
    const valuesByKey: { [dateKey: string]: number } = {}
    do {
      months.push(currentDate.format('MMM'))
      valuesByKey[currentDate.format('YYYY-MM')] = 0.0
      currentDate.add(1, 'month')
    } while (endDate.isSameOrAfter(currentDate, 'month'))

    for (const [dateKey, amount] of Object.entries(revenueArray)) {
      valuesByKey[dateKey] = amount
    }

    return [months, Object.values(valuesByKey)]
  }, [revenueArray])

  const roundedValue = values.map(value => {
    return parseFloat(value.toFixed(2))
  })

  useEffect(() => {
    const chart = chartRef.current
    if (chart) {
      const gradient = chart.ctx.createLinearGradient(0, 0, 300, 0)
      gradient.addColorStop(0, '#00ffff')
      gradient.addColorStop(0.5, '#0098ed')
      gradient.addColorStop(1, '#005cd9')
      setChartData({
        labels,
        datasets: [
          {
            label: `${t('Total revenue per month')}`,
            data: roundedValue,
            backgroundColor: gradient,
          },
        ] as ChartDataSets,
      })
    }
  }, [labels, values])

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: false,
      },
    },
  }

  const hasNoData = values.some(value => {
    return value !== 0
  })

  return (
    <DashboardCardBottomLeftContainer>
      <DashBoardCardTitle>{t('Revenue').toUpperCase()}</DashBoardCardTitle>
      <RevenueCardContainer windowHeight={windowHeight}>
        {hasNoData ? (
          <Bar ref={chartRef} data={chartData} options={options} />
        ) : (
          <NoRevenueLabel>
            {t('There is no revenue data available')}📊
          </NoRevenueLabel>
        )}
      </RevenueCardContainer>
    </DashboardCardBottomLeftContainer>
  )
}
